import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';
import {
  getDashboardSummary,
  getRecentOrders,
  getAllOrders,
  updateOrderStatus,
  getRevenueAnalytics
} from '../services/adminService';
import { 
  addProduct, 
  updateProduct, 
  deleteProduct, 
  fetchAdminProducts, 
  uploadImageToSupabase 
} from '../services/productService';
import 'chart.js/auto';
import { Line } from 'react-chartjs-2';
import './AdminDashboard.css'

const logo = '/logo_file_page-0001.png';

const CATEGORY_MAP = {
  "Men's Wear": ["Shirts", "Blazers/Jackets", "Kurtas", "Trousers", "Co-ords"],
  "Women's Wear": ["Dresses", "Corsets", "Tops/Tunics", "Shirts/Blouses", "Skirts/Trousers", "Co-ords", "Kurtas"]
};

// Flat list of individual piece names for the "Individual Pieces" dropdown,
// derived from CATEGORY_MAP but with combined entries (e.g. "Skirts/Trousers")
// split into their own separate options.
const PIECE_OPTIONS = Array.from(
  new Set(
    Object.values(CATEGORY_MAP)
      .flat()
      .flatMap(cat => cat.split('/'))
  )
);

const AdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [stats, setStats] = useState({
    totalSales: 0,
    totalOrders: 0,
    visitors: 0,
    cancelledOrders: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [revenueData, setRevenueData] = useState(null);

  const [products, setProducts] = useState([]);
  const [productsPage, setProductsPage] = useState(1);
  const PRODUCTS_PER_PAGE = 10;
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');

  const initialProductState = {
    name: '',
    price: '',
    main_category: '',
    sub_category: '',
    description: '',
    is_featured: false,
    is_live: true,
    collection: 'kok',
    hasPieces: false,
    pieces: [{ label: '', price: '' }],
    sizes: [
      { size: 'S', stock: '' },
      { size: 'M', stock: '' },
      { size: 'L', stock: '' },
      { size: 'XL', stock: '' }
    ],
    existingImages: [],
    newFiles: []
  };
  
  const [currentProduct, setCurrentProduct] = useState(initialProductState);

  const [allOrders, setAllOrders] = useState([]);
  const [ordersPage, setOrdersPage] = useState(1);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [summary, recent, revenue] = await Promise.all([
        getDashboardSummary(),
        getRecentOrders(),
        getRevenueAnalytics()
      ]);

      setStats({
        totalSales: summary?.data?.total_sales || summary?.data?.totalSales || summary?.data?.total_revenue || 0,
        totalOrders: summary?.data?.total_orders || summary?.data?.totalOrders || 0,
        visitors: summary?.data?.total_visitors || summary?.data?.visitors || 0,
        cancelledOrders: summary?.data?.order_overview?.cancelled_orders || 0 
      });
      setRecentOrders(recent?.data || recent?.orders || []);
      setRevenueData(revenue?.data || revenue);
    } catch (error) {
      console.error('Dashboard load failed', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdminProducts({ page: productsPage, limit: PRODUCTS_PER_PAGE });
      setProducts(data.products || []);
    } catch (error) {
      console.error('Load products failed', error);
    } finally {
      setLoading(false);
    }
  }, [productsPage]);

  const loadAllOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllOrders(ordersPage, 10);
      setAllOrders(data.orders || data.data || []);
    } catch (error) {
      console.error('Load orders failed', error);
    } finally {
      setLoading(false);
    }
  }, [ordersPage]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (activeTab === 'products') loadProducts();
    if (activeTab === 'orders') loadAllOrders();
  }, [activeTab, loadProducts, loadAllOrders]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setIsSidebarOpen(false);
  };

  const handleSizeChange = (index, field, value) => {
    const updatedSizes = [...currentProduct.sizes];
    if (field === 'stock') {
      updatedSizes[index].stock = value === '' ? '' : parseInt(value);
    } else {
      updatedSizes[index].size = value; 
    }
    setCurrentProduct({ ...currentProduct, sizes: updatedSizes });
  };

  const handleAddSize = () => {
    setCurrentProduct(prev => ({
      ...prev,
      sizes: [...prev.sizes, { size: '', stock: 0 }]
    }));
  };

  const handleRemoveSize = (index) => {
    const updatedSizes = [...currentProduct.sizes];
    updatedSizes.splice(index, 1);
    setCurrentProduct(prev => ({ ...prev, sizes: updatedSizes }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newFileObjects = files.map(file => ({
        file: file,
        preview: URL.createObjectURL(file),
        id: Date.now() + Math.random()
      }));

      setCurrentProduct(prev => ({
        ...prev,
        newFiles: [...prev.newFiles, ...newFileObjects]
      }));
    }
  };

  const handleRemoveImage = (type, index) => {
    setCurrentProduct(prev => {
      if (type === 'existing') {
        const updated = [...prev.existingImages];
        updated.splice(index, 1);
        return { ...prev, existingImages: updated };
      } else {
        const updated = [...prev.newFiles];
        updated.splice(index, 1);
        return { ...prev, newFiles: updated };
      }
    });
  };

  // WORKAROUND for a backend bug: editing a product's category through the
  // normal update API doesn't actually update the product_categories join
  // table (it only gets set correctly when a product is first created).
  // This writes the correct category links directly to Supabase instead,
  // looking up the right category IDs live so it stays correct even as
  // new categories get added later.
  const fixProductCategoryLink = async (productId, mainCategoryName, subCategoryName) => {
    if (!supabase || !productId) return;
    try {
      const { data: mainCat, error: mainErr } = await supabase
        .from('categories')
        .select('id')
        .eq('name', mainCategoryName)
        .eq('type', 'main')
        .maybeSingle();
      if (mainErr) throw mainErr;

      const { data: subCat, error: subErr } = await supabase
        .from('categories')
        .select('id')
        .eq('name', subCategoryName)
        .eq('type', 'sub')
        .eq('parent_id', mainCat?.id)
        .maybeSingle();
      if (subErr) throw subErr;

      const categoryIds = [mainCat?.id, subCat?.id].filter(Boolean);
      if (categoryIds.length === 0) return;

      // Remove old links, then insert the correct current ones
      await supabase.from('product_categories').delete().eq('product_id', productId);
      await supabase.from('product_categories').insert(
        categoryIds.map((category_id) => ({ product_id: productId, category_id }))
      );
    } catch (err) {
      console.error('Failed to fix product category link in Supabase:', err);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const uploadPromises = currentProduct.newFiles.map(item => 
        uploadImageToSupabase(item.file)
      );
      const newUploadedUrls = await Promise.all(uploadPromises);

      const allImages = [
        ...currentProduct.existingImages.map((img, i) => ({
          image_url: img.image_url || img, 
          alt_text: currentProduct.name,
          sort_order: i + 1
        })),
        ...newUploadedUrls.map((url, i) => ({
          image_url: url,
          alt_text: currentProduct.name,
          sort_order: currentProduct.existingImages.length + i + 1
        }))
      ];

      const totalStock = currentProduct.sizes.reduce((sum, s) => sum + Number(s.stock || 0), 0);

      const cleanName = currentProduct.name.replace(/^tot\s*-\s*/i, '').trim();
      const finalName = currentProduct.collection === 'tot' ? `ToT - ${cleanName}` : cleanName;

      const cleanDescription = (currentProduct.description || '').replace(/\[\[PIECES_DATA:.*?\]\]/s, '').trim();
      let finalDescription = cleanDescription;
      if (currentProduct.hasPieces) {
        const validPieces = currentProduct.pieces.filter(p => p.label && p.price);
        if (validPieces.length > 0) {
          const piecesData = {
            fullSet: Number(currentProduct.price),
            pieces: validPieces.map(p => ({ label: p.label, price: Number(p.price) }))
          };
          const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(piecesData))));
          finalDescription = `${cleanDescription}\n\n[[PIECES_DATA:${encoded}]]`;
        }
      }
      if (!currentProduct.is_live) {
        finalDescription = `${finalDescription}\n\n[[HIDDEN]]`;
      }

      const payload = {
        name: finalName,
        description: finalDescription,
        price: Math.round(Number(currentProduct.price)),
        stock: totalStock,
        main_category: currentProduct.main_category,
        sub_category: currentProduct.sub_category,
        sizes: currentProduct.sizes.map(s => ({
          size: s.size,
          stock: s.stock === '' ? 0 : Number(s.stock) 
        })),
        is_featured: Boolean(currentProduct.is_featured),
        images: allImages
      };

      console.log("Sending data to backend:", payload);

      let savedProductId = currentProduct.id;
      if (isEditingProduct) {
        await updateProduct(currentProduct.id, payload);
      } else {
        const created = await addProduct(payload);
        savedProductId = created?.id || created?.product?.id || created?.data?.id;
      }

      // Directly fix the category link in Supabase (see comment on the
      // helper above for why this is needed)
      await fixProductCategoryLink(savedProductId, currentProduct.main_category, currentProduct.sub_category);

      setIsProductModalOpen(false);
      resetProductForm();
      loadProducts();
      alert(isEditingProduct ? 'Product Updated Successfully!' : 'Product Added Successfully!');
    } catch (error) {
      console.error("Submission Error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLive = async (product, e) => {
    if (e) e.stopPropagation();
    const nextIsLive = !product.isLive;
    const previousProducts = [...products];
    setProducts(prev => prev.map(p => (p.id === product.id ? { ...p, isLive: nextIsLive } : p)));

    try {
      const cleanDescription = (product.rawDescription || '').replace(/\n*\[\[HIDDEN\]\]/i, '').trim();
      const finalDescription = nextIsLive ? cleanDescription : `${cleanDescription}\n\n[[HIDDEN]]`;

      await updateProduct(product.id, {
        name: product.rawName || product.name,
        description: finalDescription,
        price: Math.round(Number(product.price)),
        stock: product.stock,
        main_category: product.category,
        sub_category: product.subCategory,
        sizes: product.sizes,
        is_featured: Boolean(product.isFeatured),
        images: product.images
      });
    } catch (error) {
      setProducts(previousProducts);
      console.error('Visibility toggle failed:', error);
      alert(`❌ Error: Could not update visibility - ${error.message}`);
    }
  };

  const handleDeleteProduct = async (id, e) => {
    if (e) e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }
    const previousProducts = [...products];
    setProducts(prevProducts => prevProducts.filter(p => p.id !== id));
    try {
      await deleteProduct(id);
      setSelectedProduct(null);
      alert('✅ Success: Product removed.');

    } catch (error) {
      setProducts(previousProducts);
      console.error('Delete failed:', error);
      alert('❌ Error: Could not delete product. It has been restored to the list.');
    }
  };

  const openEditModal = (product, e) => {
    if (e) e.stopPropagation();
    
    console.log("Opening product:", product.name);
    console.log("Raw images:", product.images);

    const defaultSizes = [
      { size: 'S', stock: 0 }, { size: 'M', stock: 0 },
      { size: 'L', stock: 0 }, { size: 'XL', stock: 0 }
    ];

    const productSizes = product.sizes && product.sizes.length > 0 
      ? product.sizes 
      : defaultSizes;

    let initialImages = [];
    let rawImages = product.images;

    if (typeof rawImages === 'string') {
      try {
        if (rawImages.trim().startsWith('[') || rawImages.trim().startsWith('{')) {
           rawImages = JSON.parse(rawImages);
        } else {
           rawImages = [{ image_url: rawImages }];
        }
      } catch (err) {
        console.warn("Failed to parse image JSON:", err);
        rawImages = [{ image_url: rawImages }];
      }
    }

    if (Array.isArray(rawImages) && rawImages.length > 0) {
      initialImages = rawImages.map(img => {
        if (typeof img === 'string') return { image_url: img };
        return { 
          image_url: img.image_url || img.url || img.src || '', 
          ...img 
        };
      }).filter(img => img.image_url);
    } 
    else if (product.image) {
      initialImages = [{ image_url: product.image }];
    }

    const totProductPrefix = /^tot\s*-\s*/i;
    const isTotProduct = totProductPrefix.test(product.rawName || product.name || '');

    const rawDescription = product.rawDescription || product.description || '';
    const isHiddenProduct = /\[\[HIDDEN\]\]/i.test(rawDescription);
    const descriptionWithoutHidden = rawDescription.replace(/\n*\[\[HIDDEN\]\]/i, '').trim();
    const piecesMatch = descriptionWithoutHidden.match(/\[\[PIECES_DATA:(.*?)\]\]/s);
    let parsedPieces = { hasPieces: false, pieces: [{ label: '', price: '' }] };
    let cleanDescription = descriptionWithoutHidden;
    if (piecesMatch) {
      try {
        let data;
        try {
          data = JSON.parse(decodeURIComponent(escape(atob(piecesMatch[1]))));
        } catch (e) {
          data = JSON.parse(piecesMatch[1]); // fallback for older plain-JSON format
        }
        parsedPieces = {
          hasPieces: true,
          pieces: data.pieces?.length > 0 ? data.pieces : [{ label: '', price: '' }]
        };
      } catch (e) {
        console.error('Failed to parse pieces data', e);
      }
      cleanDescription = descriptionWithoutHidden.replace(/\[\[PIECES_DATA:.*?\]\]/s, '').trim();
    }

    setCurrentProduct({
      id: product.id,
      name: (product.name || '').replace(totProductPrefix, '').trim(),
      price: product.price,
      main_category: product.category || product.mainCategory || "Men's Wear",
      sub_category: product.subCategory || "",
      description: cleanDescription,
      is_featured: product.isFeatured || false,
      is_live: !isHiddenProduct,
      collection: isTotProduct ? 'tot' : 'kok',
      hasPieces: parsedPieces.hasPieces,
      pieces: parsedPieces.pieces,
      sizes: productSizes,
      existingImages: initialImages,
      newFiles: []
    });

    setIsEditingProduct(true);
    setIsProductModalOpen(true);
    setSelectedProduct(null);
  };

  const resetProductForm = () => {
    setCurrentProduct(initialProductState);
    setIsEditingProduct(false);
  };

  const handleProductRowClick = (product) => {
    setSelectedProduct(product);
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    const previousOrders = [...allOrders];
    const previousRecent = [...recentOrders];

    const updater = (orders) => 
      orders.map(order => 
        (order.id === orderId || order.order_id === orderId) 
          ? { ...order, order_status: newStatus } 
          : order
      );

    setAllOrders(updater(allOrders));
    setRecentOrders(updater(recentOrders));

    try {
      await updateOrderStatus(orderId, newStatus);
      alert(`✅ Success: Order #${orderId.substring(0, 8)} is now ${newStatus.toUpperCase()}`);
    } catch (error) {
      setAllOrders(previousOrders);
      setRecentOrders(previousRecent);
      console.error('Update failed:', error);
      alert(`❌ Error: ${error.message || 'Could not update status'}`);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const parseRevenueData = () => {
    if (!revenueData) {
      return { labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'], data: [0, 0, 0, 0, 0, 0] };
    }
    const months = revenueData.months || revenueData.data?.months || revenueData.labels || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const revenue = revenueData.revenue || revenueData.data?.revenue || revenueData.values || revenueData.data || [0, 0, 0, 0, 0, 0];
    return { labels: months, data: Array.isArray(revenue) ? revenue : [0, 0, 0, 0, 0, 0] };
  };

  const parsedRevenue = parseRevenueData();
  
  const chartData = {
    labels: parsedRevenue.labels,
    datasets: [{
      label: 'Revenue (₹)',
      data: parsedRevenue.data,
      borderColor: '#6F3132',
      backgroundColor: 'rgba(111, 49, 50, 0.1)',
      fill: true,
      tension: 0.4,
      pointBackgroundColor: '#6F3132',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4
    }]
  };

  if (loading && activeTab === 'overview' && !stats.totalSales) {
    return <div className="admin-loading">Loading Dashboard...</div>;
  }

  const filteredOrders = allOrders.filter(order => {
    const searchTerm = orderSearchTerm.toLowerCase();
    const matchesSearch = 
      (order.customer_name?.toLowerCase() || '').includes(searchTerm) ||
      (order.order_number?.toLowerCase() || '').includes(searchTerm) ||
      (order.order_id?.toLowerCase() || '').includes(searchTerm);

    const matchesStatus = orderStatusFilter === 'all' || 
      order.order_status?.toLowerCase() === orderStatusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="admin-dashboard">
      <div className="mobile-header">
        <button className="menu-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
          ☰
        </button>
        <img src={logo} alt="Khaddar" className="mobile-header-logo" />
        <div style={{ width: '30px' }}></div> 
      </div>

      <aside className={`admin-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="admin-logo">
          <img src={logo} alt="Khaddar" />
          <span>ADMIN</span>
        </div>
        <nav className="admin-nav">
          <button
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => handleTabChange('overview')}
          >
            Dashboard
          </button>
          <button
            className={activeTab === 'products' ? 'active' : ''}
            onClick={() => handleTabChange('products')}
          >
            Products
          </button>
          <button
            className={activeTab === 'orders' ? 'active' : ''}
            onClick={() => handleTabChange('orders')}
          >
            Orders
          </button>
          <button onClick={() => { logout(); navigate('/'); }} className="logout-btn">
            Logout
          </button>
        </nav>
      </aside>
      
      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>
      )}

      <main className="admin-content">
        {activeTab === 'overview' && (
          <div className="dashboard-content fade-in">
            <header className="page-header">
              <h1>Overview</h1>
              <p>Welcome back, Admin</p>
            </header>

            <div className="stats-grid">
              <div className="stat-card">
                <h3>Total Sales</h3>
                <p className="stat-value">{formatCurrency(stats.totalSales)}</p>
              </div>
              <div className="stat-card">
                <h3>Orders</h3>
                <p className="stat-value">{stats.totalOrders}</p>
              </div>
              <div className="stat-card">
                <h3>Registered Users</h3>
                <p className="stat-value">{stats.visitors}</p>
                <span className="stat-label">Logged-in visitors</span>
              </div>
              <div className="stat-card">
                <h3>Cancelled Orders</h3>
                <p className="stat-value">{stats.cancelledOrders}</p>
              </div>
            </div>

            <div className="chart-section">
              <h2>Revenue Analytics</h2>
              <div className="chart-wrapper">
                <Line data={chartData} options={{ responsive: true, maintainAspectRatio: false }} />
              </div>
            </div>

            <div className="recent-orders-section">
              <h2>Recent Orders</h2>
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map(order => (
                      <tr key={order.id || order.order_id}>
                        <td>#{order.order_number || (order.order_id ? order.order_id.substring(0, 8) : 'N/A')}</td>
                        <td>{order.customer_name || 'Guest'}</td>
                        <td>{new Date(order.created_at || order.orderDate).toLocaleDateString()}</td>
                        <td>{formatCurrency(order.total_amount || order.total)}</td>
                        <td>
                          <span className={`status-badge ${order.order_status?.toLowerCase()}`}>
                            {order.order_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="products-content fade-in">
            <header className="page-header">
              <h1>Products</h1>
              <button
                className="primary-btn"
                onClick={() => { resetProductForm(); setIsProductModalOpen(true); }}
              >
                + Add Product
              </button>
            </header>

            <div className="table-responsive">
              <table className="admin-table hover-rows">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, index) => (
                    <tr
                      key={product.id}
                      onClick={() => handleProductRowClick(product)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{(productsPage - 1) * PRODUCTS_PER_PAGE + (index + 1)}</td>
                      <td>
                         <img 
                            src={Array.isArray(product.images) && product.images.length > 0 ? (product.images[0].image_url || product.images[0].url || product.images[0]) : product.image} 
                            alt={product.name} 
                            className="product-thumb" 
                          />
                      </td>
                      <td>{product.name}</td>
                      <td>{product.category} ({product.subCategory})</td>
                      <td>{formatCurrency(product.price)}</td>
                      <td>
                        <span className={`stock-badge ${product.stock < 10 ? 'low' : 'good'}`}>
                          {product.stock}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="action-buttons">
                          <label className="live-toggle-label" title={product.isLive ? 'Live on site - untick to hide' : 'Hidden from site - tick to make live'}>
                            <input
                              type="checkbox"
                              checked={!!product.isLive}
                              onChange={(e) => handleToggleLive(product, e)}
                            />
                            Live
                          </label>
                          <button className="action-btn edit" onClick={(e) => openEditModal(product, e)}>Edit</button>
                          <button className="action-btn delete" onClick={(e) => handleDeleteProduct(product.id, e)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {products.length === 0 && (
                    <tr><td colSpan="7" className="text-center">No products found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button disabled={productsPage === 1 || loading} onClick={() => setProductsPage(p => Math.max(1, p - 1))} style={{ opacity: (productsPage === 1 || loading) ? 0.5 : 1 }}>Previous</button>
              <span>Page {productsPage}</span>
              <button disabled={products.length < PRODUCTS_PER_PAGE || loading} onClick={() => setProductsPage(p => p + 1)} style={{ opacity: (products.length < PRODUCTS_PER_PAGE || loading) ? 0.5 : 1 }}>Next</button>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="orders-content fade-in">
            <header className="page-header">
                <div className="orders-header-row" style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '10px',
                  width: '100%',
                  flexWrap: 'wrap'
                }}>
                  <h1 style={{ margin: 0 }}>Manage Orders</h1>

                  <div className="table-controls" style={{ display: 'flex', gap: '15px', alignItems: 'center', marginLeft : 'auto'}}>
                    <input
                      type="text"
                      placeholder="Search by ID or Name..."
                      value={orderSearchTerm}
                      onChange={(e) => setOrderSearchTerm(e.target.value)}
                      style={{ 
                        padding: '8px 15px', 
                        borderRadius: '5px', 
                        border: '1px solid #ddd', 
                        width: '220px',
                        fontSize: '14px'
                      }}
                    />
                    <select
                      value={orderStatusFilter}
                      onChange={(e) => setOrderStatusFilter(e.target.value)}
                      style={{ 
                        padding: '8px 12px', 
                        borderRadius: '5px', 
                        border: '1px solid #ddd', 
                        minWidth: '150px',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="all">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
            </header>

            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map(order => (
                    <tr key={order.id || order.order_id}>
                      <td>#{order.order_number || (order.order_id ? order.order_id.substring(0, 8) : 'N/A')}</td>
                      <td>{order.customer_name || 'Guest'}</td>
                      <td>{new Date(order.created_at || order.orderDate).toLocaleDateString()}</td>
                      <td>{formatCurrency(order.total_amount || order.total)}</td>
                      <td>
                        <select
                          value={order.order_status?.toLowerCase() || 'pending'}
                          onChange={(e) => handleStatusUpdate(order.order_id || order.id, e.target.value)}
                          className="status-select"
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="pagination">
              <button disabled={ordersPage === 1 || loading} onClick={() => setOrdersPage(p => Math.max(1, p - 1))} style={{ opacity: (ordersPage === 1 || loading) ? 0.5 : 1 }}>Previous</button>
              <span>Page {ordersPage}</span>
              <button disabled={allOrders.length < 10 || loading} onClick={() => setOrdersPage(p => p + 1)} style={{ opacity: (allOrders.length < 10 || loading) ? 0.5 : 1 }}>Next</button>
            </div>
          </div>
        )}
      </main>

      {isProductModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content admin-modal">
            <h2>{isEditingProduct ? 'Edit Product' : 'Add New Product'}</h2>
            <form onSubmit={handleProductSubmit}>
              <div className="form-group">
                <label>Product Name</label>
                <input type="text" value={currentProduct.name} onChange={(e) => setCurrentProduct({ ...currentProduct, name: e.target.value })} required />
              </div>

              <div className="form-group">
                <label>Collection</label>
                <select
                  value={currentProduct.collection}
                  onChange={(e) => setCurrentProduct({ ...currentProduct, collection: e.target.value })}
                >
                  <option value="kok">Kolours of Kutch</option>
                  <option value="tot">Threads of Travancore</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price (₹)</label>
                  <input type="number" value={currentProduct.price} onChange={(e) => setCurrentProduct({ ...currentProduct, price: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Featured</label>
                  <select value={currentProduct.is_featured} onChange={(e) => setCurrentProduct({ ...currentProduct, is_featured: e.target.value === 'true' })}>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </div>

              <div className="form-group set-toggle-group">
                <label className="set-toggle-label">
                  <input
                    type="checkbox"
                    checked={currentProduct.is_live}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, is_live: e.target.checked })}
                  />
                  <span>Show product live on site <em>(untick to keep it hidden from customers)</em></span>
                </label>
              </div>

              <div className="form-group set-toggle-group">
                <label className="set-toggle-label">
                  <input
                    type="checkbox"
                    checked={currentProduct.hasPieces}
                    onChange={(e) => setCurrentProduct({ ...currentProduct, hasPieces: e.target.checked })}
                  />
                  <span>This product is a set <em>(customers can buy the full set OR individual pieces)</em></span>
                </label>
              </div>

              {currentProduct.hasPieces && (
                <div className="pieces-panel">
                  <p className="pieces-panel-hint">
                    The full set price uses the main Price field above. Just add prices for the individual pieces below.
                  </p>

                  <label className="pieces-list-label">Individual Pieces</label>
                  <div className="pieces-list">
                    {currentProduct.pieces.map((piece, idx) => (
                      <div key={idx} className="piece-row">
                        <select
                          value={piece.label}
                          onChange={(e) => {
                            const updated = [...currentProduct.pieces];
                            updated[idx].label = e.target.value;
                            setCurrentProduct({ ...currentProduct, pieces: updated });
                          }}
                          className="piece-name-input"
                        >
                          <option value="">Select piece</option>
                          {PIECE_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          placeholder="Price (₹)"
                          value={piece.price}
                          onChange={(e) => {
                            const updated = [...currentProduct.pieces];
                            updated[idx].price = e.target.value;
                            setCurrentProduct({ ...currentProduct, pieces: updated });
                          }}
                          className="piece-price-input"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updated = currentProduct.pieces.filter((_, i) => i !== idx);
                            setCurrentProduct({ ...currentProduct, pieces: updated.length > 0 ? updated : [{ label: '', price: '' }] });
                          }}
                          className="piece-remove-btn"
                          title="Remove piece"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentProduct({ ...currentProduct, pieces: [...currentProduct.pieces, { label: '', price: '' }] })}
                    className="add-piece-btn"
                  >
                    + Add Piece
                  </button>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Main Category</label>
                  <select 
                    value={currentProduct.main_category} 
                    onChange={(e) => setCurrentProduct({ ...currentProduct, main_category: e.target.value, sub_category: '' })} 
                    required
                  >
                    <option value="">Select Category</option>
                    {Object.keys(CATEGORY_MAP).map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Sub Category</label>
                  <select 
                    value={currentProduct.sub_category} 
                    onChange={(e) => setCurrentProduct({ ...currentProduct, sub_category: e.target.value })} 
                    disabled={!currentProduct.main_category}
                    required
                  >
                    <option value="">Select Sub Category</option>
                    {currentProduct.main_category && CATEGORY_MAP[currentProduct.main_category].map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-sizes-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ margin: 0 }}>Stock & Sizes</label>
                  <button type="button" onClick={handleAddSize} style={{ background: '#6F3132', color: 'white', border: 'none', padding: '5px 10px', fontSize: '0.7rem', cursor: 'pointer', borderRadius: '4px' }}>+ Add Variant</button>
                </div>
                <div className="dynamic-sizes-list">
                  {currentProduct.sizes.map((s, idx) => (
                    <div key={idx} className="size-row">
                      <div style={{ flex: 1 }}><input type="text" placeholder="Size (e.g. XL)" value={s.size} onChange={(e) => handleSizeChange(idx, 'size', e.target.value)} required style={{ width: '100%', textAlign: 'center' }} /></div>
                      <div style={{ flex: 1 }}><input type="number" placeholder="Stock" value={s.stock === 0 ? '' : s.stock} onChange={(e) => handleSizeChange(idx, 'stock', e.target.value)} style={{ width: '100%', textAlign: 'center' }} /></div>
                      <button type="button" onClick={() => handleRemoveSize(idx)} className="remove-size-btn" title="Remove Size">&times;</button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Upload Images (Select Multiple)</label>
                <div className="image-upload-wrapper">
                  <input type="file" accept="image/*" multiple onChange={handleFileChange} />
                  <div className="image-preview-grid">
                    {currentProduct.existingImages.map((img, idx) => (
                      <div key={`exist-${idx}`} className="preview-item">
                        <img src={img.image_url || img} alt="Existing" />
                        <button type="button" className="remove-image-btn" onClick={() => handleRemoveImage('existing', idx)}>&times;</button>
                      </div>
                    ))}
                    {currentProduct.newFiles.map((item, idx) => (
                      <div key={`new-${item.id}`} className="preview-item new-file">
                        <img src={item.preview} alt="New Upload" />
                        <button type="button" className="remove-image-btn" onClick={() => handleRemoveImage('new', idx)}>&times;</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-group"><label>Description</label><textarea value={currentProduct.description} onChange={(e) => setCurrentProduct({ ...currentProduct, description: e.target.value })} rows="3" /></div>
              <div className="modal-actions"><button type="button" onClick={() => setIsProductModalOpen(false)} className="cancel-btn">Cancel</button><button type="submit" className="save-btn" disabled={loading}>{loading ? 'Saving...' : 'Save Product'}</button></div>
            </form>
          </div>
        </div>
      )}

      {selectedProduct && (
        <div className="modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="modal-content product-details-modal" onClick={e => e.stopPropagation()}>
            <div className="product-details-header">
              <h2>Product Details</h2>
              <button className="close-icon-btn" onClick={() => setSelectedProduct(null)}>&times;</button>
            </div>

            <div className="product-details-body">
              <div className="product-details-image">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', overflowY: 'auto', maxHeight: '400px' }}>
                  {(() => {
                    let images = selectedProduct.images;
                    if (typeof images === 'string') {
                      try { images = JSON.parse(images); } catch(e) { images = [images]; }
                    }
                    if (Array.isArray(images) && images.length > 0) {
                      return images.map((img, idx) => (
                        <img 
                          key={idx} 
                          src={typeof img === 'string' ? img : (img.image_url || img.url || img.src)} 
                          alt={`${selectedProduct.name} - ${idx + 1}`} 
                          style={{ width: '100%', borderRadius: '4px', border: '1px solid #eee' }} 
                        />
                      ));
                    }
                    return (
                      <img 
                        src={selectedProduct.image || ''} 
                        alt={selectedProduct.name} 
                        style={{ width: '100%', borderRadius: '4px' }} 
                      />
                    );
                  })()}
                </div>
              </div>

              <div className="product-details-info">
                <h3>{selectedProduct.name}</h3>
                <p className="product-id">ID: #{selectedProduct.id}</p>

                <div className="detail-row">
                  <span className="label">Main Category:</span>
                  <span className="value">{selectedProduct.mainCategory || selectedProduct.category}</span>
                </div>
                
                <div className="detail-row">
                  <span className="label">Sub Category:</span>
                  <span className="value">{selectedProduct.subCategory}</span>
                </div>

                <div className="detail-row">
                  <span className="label">Price:</span>
                  <span className="value price">{formatCurrency(selectedProduct.price)}</span>
                </div>

                <div className="detail-row">
                  <span className="label">Stock:</span>
                  <span className={`value stock-badge ${selectedProduct.stock < 10 ? 'low' : 'good'}`}>
                    {selectedProduct.stock} units
                  </span>
                </div>

                <div className="detail-section">
                  <span className="label">Description:</span>
                  <p className="description">{selectedProduct.description || 'No description available.'}</p>
                </div>

                <div className="details-actions">
                  <button className="primary-btn" onClick={() => openEditModal(selectedProduct)}>Edit Product</button>
                  <button className="action-btn delete" onClick={() => handleDeleteProduct(selectedProduct.id)}>Delete Product</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;